"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, Gauge, PhoneCall, Volume2, Waves } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const symptoms = [{
  id: "slab-leak",
  title: "Warm Spots on Floors",
  category: "High Urgency",
  badgeVariant: "destructive" as const,
  icon: Flame,
  description: "Unexplained warm patches on tile or hardwood floors indicate hot water line leaks beneath your concrete foundation.",
  serviceLabel: "Explore Slab & Pipe Repair",
  link: "/pipe-repair/",
  dangerLevel: "Foundation risk"
}, {
  id: "sound-pipes",
  title: "Running Water Sounds",
  category: "Hidden Leak",
  badgeVariant: "secondary" as const,
  icon: Volume2,
  description: "Hearing hissing, bubbling, or running water behind drywall when every fixture and faucet in the house is turned off.",
  serviceLabel: "Leak Detection Services",
  link: "/leak-detection-repair/",
  dangerLevel: "Active water loss"
}, {
  id: "high-bills",
  title: "Sudden Water Bill Spikes",
  category: "Costly Leak",
  badgeVariant: "outline" as const,
  icon: Gauge,
  description: "A noticeable increase in monthly water charges without an increase in household usage points directly to underground line loss.",
  serviceLabel: "Schedule Line Inspection",
  link: "/leak-detection-repair/",
  dangerLevel: "Escalating costs"
}, {
  id: "wall-dampness",
  title: "Damp Drywall or Baseboards",
  category: "Structural Risk",
  badgeVariant: "destructive" as const,
  icon: Droplets,
  description: "Soft, discolored, or peeling drywall along baseboards and ceilings usually signals pinhole copper pipe failure.",
  serviceLabel: "Pipe Repair Options",
  link: "/pipe-repair/",
  dangerLevel: "Wood rot & mold"
}, {
  id: "low-pressure",
  title: "Loss of Water Pressure",
  category: "Supply Issue",
  badgeVariant: "secondary" as const,
  icon: Waves,
  description: "A sudden drop in flow across multiple fixtures often means an active fracture in the main incoming line.",
  serviceLabel: "Emergency Plumbing Care",
  link: "/emergency-plumbing/",
  dangerLevel: "Main supply failure"
}, {
  id: "musty-smell",
  title: "Persistent Musty Odors",
  category: "Health Hazard",
  badgeVariant: "outline" as const,
  icon: AlertTriangle,
  description: "Earthy or moldy smells in bathrooms, crawl spaces, or basements indicate long-standing trapped moisture from concealed lines.",
  serviceLabel: "Contact Our Leak Specialists",
  link: "/contact/",
  dangerLevel: "Air quality hazard"
}];
export default function LeakSymptoms() {
  const prefersReduced = useReducedMotion();
  return <section id="leaksymptomsdiagnosis" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 text-xs uppercase tracking-wider font-semibold">
            Diagnostic Guide
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Common Signs of a Hidden Water Leak
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Hidden leaks cause costly property damage before water ever appears on the surface. Check your home against these common warning signs and get prompt diagnosis in Jonesboro, GA.
          </p>
        </div>

        {/* Symptoms Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {symptoms.map((symptom, i) => {
          const IconComponent = symptom.icon;
          return <motion.div key={symptom.id} data-index={i} initial={prefersReduced ? false : {
            opacity: 0,
            y: 20
          }} whileInView={prefersReduced ? undefined : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.4,
            delay: prefersReduced ? 0 : i * 0.08
          }} className="h-full flex">
                <Card className="flex flex-col justify-between w-full h-full border border-border/80 bg-card hover:border-primary/40 shadow-xs hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <IconComponent className="size-5" />
                      </div>
                      <Badge variant={symptom.badgeVariant} className="text-xs font-medium">
                        {symptom.category}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold tracking-tight text-card-foreground">
                      {symptom.title}
                    </CardTitle>
                    <div className="text-xs font-medium text-destructive mt-0.5">
                      Risk: {symptom.dangerLevel}
                    </div>
                  </CardHeader>

                  <CardContent className="pb-4">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {symptom.description}
                    </CardDescription>
                  </CardContent>

                  <CardFooter className="pt-2 border-t border-border/50 flex items-center justify-between">
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between px-2 text-primary hover:text-primary hover:bg-primary/5">
                      <Link to={symptom.link}>
                        <span>{symptom.serviceLabel}</span>
                        <ArrowRight className="size-4 ml-1.5" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Emergency Callout Box */}
        <div className="mt-12 md:mt-16 bg-muted/60 border border-border rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <h3 className="text-lg md:text-xl font-semibold text-foreground">
              Suspect an active leak in your Jonesboro property?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our 24/7 plumbers use electronic acoustic sensors and thermal cameras to find leaks without tearing up walls.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button asChild variant="default" size="lg" className="shadow-sm">
              <Link to="/leak-detection-repair/">
                Get Leak Detection
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contact/">
                <PhoneCall className="size-4 mr-2" />
                Contact Dispatch
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}